import React, { FC, useContext, useMemo, useState } from "react"
import { useSwipeable } from "react-swipeable"
import { Range } from "react-range"
import { graphql, useStaticQuery } from "gatsby"

import { BespokeComponentFragmentFragment } from "@graphqlTypes"

import { addThousandCommas, addThousandsSeperator } from "@utils"

import ContentfulLpContext from "@context/ContentfulLpContext/ContentfulLpContext"

import ToggleWithText, { TOGGLE_COLORS, TOGGLE_SIZES } from "@components/ToggleWithText/ToggleWithText"
import Hero from "@components/Hero/Hero"

import LearnMoreToggle from "./LearnMoreToggle/LearnMoreToggle"
import PricingHeroLegacyPlansPlan from "./PricingHeroLegacyPlansPlan/PricingHeroLegacyPlansPlan"

import "./PricingHeroLegacyPlans.scss"

enum LEGACY_PLAN_IDS {
  PLANS_44_51_52 = "PLANS_44_51_52",
  PLANS_44_54_55 = "PLANS_44_54_55",
}

type PricingHeroLegacyPlansJsonPayload = {
  h1: string,
  planIds: LEGACY_PLAN_IDS,
}

const PricingHeroLegacyPlans: FC<{
  contentful_id: BespokeComponentFragmentFragment["contentful_id"],
  jsonPayload: BespokeComponentFragmentFragment["jsonPayload"],
}> = ({
  contentful_id,
  jsonPayload,
}) => {
    const { PLANS_44_51_52_data, PLANS_44_54_55_data } = useStaticQuery<any>(graphql`
    query PricingHeroLegacyPlansQuery {
      PLANS_44_51_52_data: contentfulJsonData(contentful_id: {eq: "18GVCLjDajpQWblk4yb7kc"}) {
        json {
          annualDiscountPercentage
          pricingIncrements
          pricingIncrementsSales
          plans {
            ...PricingHeroPlanFragment
          }
        }
      }
      PLANS_44_54_55_data: contentfulJsonData(contentful_id: {eq: "7ey6DwDhVRFou1mIELVjsr"}) {
        json {
          annualDiscountPercentage
          pricingIncrements
          pricingIncrementsSales
          plans {
            ...PricingHeroPlanFragment
          }
        }
      }
    }
  `)

    const json = useMemo(() => {
      try {
        return JSON.parse(jsonPayload?.internal.content || "") as PricingHeroLegacyPlansJsonPayload
      } catch (e) {
        console.error(`Failed to parse JSON Payload: PricingHeroLegacyPlans (${contentful_id})`)
        return null
      }
    }, [jsonPayload])

    if (!json) return null

    const dataToUse = json.planIds === LEGACY_PLAN_IDS.PLANS_44_51_52 ? PLANS_44_51_52_data : PLANS_44_54_55_data

    if (!json || !(dataToUse && dataToUse.json)) return null

    const {
      annualDiscountPercentage,
      pricingIncrements,
      plans,
    } = dataToUse.json

    if (!(annualDiscountPercentage && pricingIncrements && plans)) return null

    const {
      isAnnualDiscountApplied,
      setIsAnnualDiscountApplied,
    } = useContext(ContentfulLpContext)

    const [activePlan, setActivePlan] = useState<number>(0)
    const [currentIncrement, setCurrentIncrement] = useState<number>(pricingIncrements[4] || 100)

    const plansToUse = plans.slice(0, 3)
    const pricingIncrementsToUse = pricingIncrements

    const currentInputValue = pricingIncrementsToUse.indexOf(currentIncrement)

    const swipeHandlers = useSwipeable({
      onSwipedLeft: () => {
        setActivePlan(currentActivePlan => {
          if (plansToUse && currentActivePlan === plansToUse.length - 1) return currentActivePlan
          return currentActivePlan + 1
        })
      },
      onSwipedRight: () => {
        setActivePlan(currentActivePlan => {
          if (currentActivePlan === 0) return currentActivePlan
          return currentActivePlan - 1
        })
      },
      preventScrollOnSwipe: true
    })

    return (
      <Hero id="PricingHero" className="PricingHeroLegacyPlans">
        <div className="container">
          <h1 className="type--headline-bold-6XL" dangerouslySetInnerHTML={{ __html: json.h1 }} />
        </div>
        <div className="container">
          <div className="grid">
            <div className="column column--1-4-L contacts-slider">
              <div className="toggle-wrapper">
                <div>
                  <ToggleWithText
                    color={TOGGLE_COLORS.ORANGE}
                    options={[
                      {
                        title: "Monthly",
                        onClick: () => setIsAnnualDiscountApplied && setIsAnnualDiscountApplied(false),
                        isActive: !isAnnualDiscountApplied,
                      },
                      {
                        title: "Annual",
                        onClick: () => setIsAnnualDiscountApplied && setIsAnnualDiscountApplied(true),
                        isActive: isAnnualDiscountApplied,
                      },
                    ]}
                    size={TOGGLE_SIZES.DYNAMIC}
                  />
                  {/* <span className={`annual-discount-badge type--text-medium-XS${isAnnualDiscountApplied ? " is-visible" : ""}`}>
                  -{annualDiscountPercentage}%
                </span> */}
                </div>
              </div>
              <div className="contacts-slider--content">
                <h6 className="current-increment type--headline-medium-S">
                  <span>{addThousandsSeperator(currentIncrement)}</span> <span>contacts helped</span>
                </h6>
                <div className="range-input-wrapper">
                  <Range
                    max={pricingIncrementsToUse.length - 1}
                    min={0}
                    onChange={(values) => {
                      const _newIncrement = pricingIncrementsToUse[Number(values[0])]
                      if (_newIncrement === null) return
                      setCurrentIncrement(_newIncrement)
                    }}
                    renderThumb={({ props }) => (
                      <div
                        {...props}
                        className="range-input--thumb"
                      />
                    )}
                    renderTrack={({ props, children }) => (
                      <div
                        {...props}
                        className="range-input--track"
                        style={{
                          "--Range--indicator-width": `${currentInputValue / (pricingIncrementsToUse.length - 1) * 100}%`,
                        }}
                      >
                        {children}
                      </div>
                    )}
                    step={1}
                    values={[currentInputValue]}
                  />
                </div>
                <div className="range-legend">
                  <span>0</span>
                  <span>{addThousandCommas(pricingIncrementsToUse[pricingIncrementsToUse.length - 1] || 0)}</span>
                </div>
                <p className="ticket-count type--text-S margin-bottom--0">
                  <span>{addThousandCommas(Math.floor(currentIncrement * 1.5))}</span>
                  emails <em>or</em> tickets per month for most&nbsp;customers.
                </p>
                <LearnMoreToggle />
              </div>
            </div>
            <div className="column column--4-13-L pricing">
              <div
                className={`flex active-plan--${activePlan}`}
                style={{
                  // OVERRIDDEN at BREAKPOINT >635px in the CSS
                  translate:
                    activePlan === 1 ?
                      `calc(var(--container-width) / 2 + var(--PricingHeroLegacyPlansPlan--card-width) * -1.5 - var(--PricingHeroLegacyPlans--plans-gap))` :
                      activePlan === 2 ?
                        `calc(var(--container-width) + var(--PricingHeroLegacyPlansPlan--card-width) * -3 - var(--PricingHeroLegacyPlans--plans-gap) * 2)` :
                        `0`
                }}
                {...swipeHandlers}
              >
                {plansToUse.map((plan, i) => !plan ? null : (
                  <PricingHeroLegacyPlansPlan
                    key={plan.id || i}
                    annualDiscountPercentage={annualDiscountPercentage}
                    currentIncrement={currentIncrement}
                    isAnnualDiscountApplied={isAnnualDiscountApplied}
                    onClick={() => setActivePlan(i)}
                    {...plan}
                  />

                ))}
              </div>
            </div>
          </div>
        </div>
      </Hero >
    )
  }

export default PricingHeroLegacyPlans
